import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone, Lock } from "lucide-react";

const NAV = [
  { to: "/", label: "Ana Sayfa", end: true },
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/branslar", label: "Branşlar" },
  { to: "/kurslar", label: "Kurslar" },
  { to: "/etkinlikler", label: "Etkinlikler" },
  { to: "/antrenor-basvuru", label: "Antrenör Başvuru" },
  { to: "/galeri", label: "Galeri" },
  { to: "/duyurular", label: "Duyurular" },
  { to: "/iletisim", label: "İletişim" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-border"
          : "bg-background/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-x flex h-[80px] md:h-[104px] items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/30 blur-2xl rounded-full scale-110" />
            <img
              src="/assets/fcs-logo.png"
              alt="FCS Focus Spor Kulübü Logo"
              className="relative h-14 w-14 md:h-20 md:w-20 object-contain drop-shadow-[0_0_24px_hsl(202_100%_50%/0.7)]"
            />
          </div>
          <div className="hidden sm:block leading-none">
            <div className="font-display text-xl md:text-2xl font-black uppercase tracking-wider">FOCUS</div>
            <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.32em] text-primary mt-1">Spor Kulübü</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to.replace(/\//g, "-") || "home"}`}
              className={({ isActive }) =>
                `relative px-2.5 py-2 font-display text-[13px] font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${
                  isActive ? "text-primary" : "text-foreground/85 hover:text-primary"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {n.label}
                  {isActive && (
                    <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-primary" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <a
            href="tel:+905078664360"
            data-testid="header-phone-link"
            className="hidden xl:inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-3 py-2 font-display text-xs font-bold uppercase tracking-wider whitespace-nowrap hover:border-primary hover:text-primary transition-colors"
          >
            <Phone className="h-3.5 w-3.5" />
            0507 866 43 60
          </a>
          <Link
            to="/admin/login"
            data-testid="header-admin-link"
            className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 font-display text-xs font-bold uppercase tracking-wider whitespace-nowrap text-primary-foreground hover:bg-[hsl(202_100%_60%)] transition-colors"
          >
            <Lock className="h-3.5 w-3.5" />
            Yönetici Girişi
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-md border border-border bg-secondary hover:border-primary hover:text-primary transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl"
          data-testid="mobile-nav"
        >
          <nav className="container-x py-4 grid gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace(/\//g, "-") || "home"}`}
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 py-3 rounded-md font-display text-base font-bold uppercase tracking-wider border border-transparent transition-colors ${
                    isActive
                      ? "text-primary bg-primary/10 border-primary/30"
                      : "text-foreground hover:bg-secondary"
                  }`
                }
              >
                <span>{n.label}</span>
                <span className="text-xs text-muted-foreground">→</span>
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              data-testid="mobile-admin-link"
              className="mt-3 flex items-center justify-center gap-2 px-3 py-3 rounded-md bg-primary text-primary-foreground font-display text-base font-bold uppercase tracking-wider"
            >
              <Lock className="h-4 w-4" />
              Yönetici Girişi
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
